import { useMutation, useQuery } from '@tanstack/react-query';
import { Mail, UserPlus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InviteDto } from '@/Domain/api/AI/dto/Invite.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/Domain/components/Shared/Card/Card';
import { QuickchatAiIcon } from '@/Domain/components/icons/QuickchatAiWordmark.icon';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';

const goToScenario = (scenarioId: string): void => {
  // Full reload so the JWT carries the new scenario role.
  window.location.assign(`/i/${scenarioId}/`);
};

export function AcceptInvitationPage() {
  const { t } = useTranslation('accept_invitation');
  const { capture } = usePostHogAnalytics();
  const [busyToken, setBusyToken] = useState<string | null>(null);

  const invitesQuery = useQuery<InviteDto[]>({
    queryKey: ['my-invites'],
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const acceptMutation = useMutation<unknown, unknown, string>({
    mutationKey: ['accept-invite'],
  });

  const ignoreMutation = useMutation<{ scenario_id: string }, unknown, void>({
    mutationKey: ['ignore-invite'],
  });

  const hasFiredViewedRef = useRef(false);
  useEffect(() => {
    if (hasFiredViewedRef.current || invitesQuery.isLoading || !invitesQuery.data) return;
    hasFiredViewedRef.current = true;
    capture(PostHogEventEnum.InvitationLandingViewed, {
      pending_invitation_count: invitesQuery.data.length,
    });
  }, [invitesQuery.isLoading, invitesQuery.data, capture]);

  // If we landed here with no pending invites (e.g. they were all accepted
  // in another tab between the redirect and this query firing), bounce to
  // the root. Done in an effect — not during render — so StrictMode's
  // double-invoke doesn't fire window.location.assign twice and so the
  // navigation doesn't happen mid-render.
  useEffect(() => {
    if (invitesQuery.isLoading) return;
    if ((invitesQuery.data?.length ?? 0) === 0) {
      window.location.assign('/');
    }
  }, [invitesQuery.isLoading, invitesQuery.data]);

  const handleAccept = async (invite: InviteDto): Promise<void> => {
    if (busyToken !== null || ignoreMutation.isPending) return;
    setBusyToken(invite.token);
    try {
      await acceptMutation.mutateAsync(invite.token);
      capture(PostHogEventEnum.InvitationAccepted, {
        scenario_id: invite.scenario_id,
        role: invite.role,
        pending_invitation_count: invitesQuery.data?.length ?? 0,
        has_inviter_email: invite.inviter_email != null,
      });
      goToScenario(invite.scenario_id);
    } catch (error) {
      console.error('Error accepting invite:', error);
      setBusyToken(null);
    }
  };

  const handleIgnore = async (): Promise<void> => {
    if (busyToken !== null || ignoreMutation.isPending) return;
    try {
      const data = await ignoreMutation.mutateAsync();
      capture(PostHogEventEnum.InvitationIgnored, {
        scenario_id: data.scenario_id,
        pending_invitation_count: invitesQuery.data?.length ?? 0,
      });
      goToScenario(data.scenario_id);
    } catch (error) {
      console.error('Error ignoring invitation:', error);
    }
  };

  if (invitesQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-foreground">
        <div className="text-muted-foreground">{t('loading')}</div>
      </div>
    );
  }

  const invites = invitesQuery.data ?? [];

  if (invites.length === 0) {
    // The empty-state useEffect above has already scheduled a redirect to /.
    return null;
  }

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center bg-background p-6 text-foreground">
      <QuickchatAiIcon
        className="absolute top-6 left-6 scale-[1.2] text-foreground"
        data-testid="accept-invitation-page-logo"
      />

      <div className="w-full max-w-xl space-y-6">
        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-semibold">{t('title')}</h1>
          <p className="text-muted-foreground">
            {invites.length === 1 ? t('subtitle_singular') : t('subtitle_plural')}
          </p>
        </div>

        <div className="space-y-3" data-testid="accept-invitation-list">
          {invites.map((invite) => {
            const inviterLabel = invite.inviter_email ?? t('inviter_fallback');
            const agentName = invite.one_word_description || t('untitled_agent');
            return (
              <Card key={invite.token} data-testid={`invite-card-${invite.token}`}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <UserPlus className="size-4 text-muted-foreground" />
                    {agentName}
                  </CardTitle>
                  <CardDescription className="flex items-center gap-2">
                    <Mail className="size-4 shrink-0" />
                    <span>
                      {t('invited_by', { inviter: inviterLabel })}
                      <span className="ml-2 rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                        {invite.role}
                      </span>
                    </span>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    variant="dark"
                    className="w-full"
                    disabled={busyToken === invite.token}
                    onClick={() => void handleAccept(invite)}
                    data-testid={`accept-invite-${invite.token}`}
                  >
                    {busyToken === invite.token ? t('accepting') : t('accept')}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="flex flex-col items-center gap-2 pt-4">
          <Button
            variant="ghost"
            disabled={busyToken !== null || ignoreMutation.isPending}
            onClick={() => void handleIgnore()}
            data-testid="ignore-invitations"
          >
            {ignoreMutation.isPending ? t('ignoring') : t('ignore')}
          </Button>
          <p className="text-center text-xs text-muted-foreground">{t('ignore_hint')}</p>
        </div>
      </div>
    </div>
  );
}
